import type { MetaIntegrationStatus, UserProfile } from '@/lib/types';

export interface PlatformConnections {
  /** Google Ads holds a working connection: a refresh token exists (#5936) and it isn't
   * known-revoked (#5935). Says nothing about *account selection* — that stays with
   * `googleAdsAccountAtom`; connected-but-unselected keeps the SELECT ACCOUNT flow. */
  googleConnected: boolean;
  /** The connection exists but the token is known-revoked (#5935) — show "reconnect"
   * wording instead of "connect". Always false when googleConnected is true. */
  googleBroken: boolean;
  /** Meta is usable for campaign work: OAuth connected + an ad account selected, the same
   * conditions the Meta wizard enforces. The org's entitlement is the backend's
   * ``meta-integration-enabled`` flag: off, the status query is refused (403) and reads as not connected (#6678). */
  metaConnected: boolean;
  /** Either signal is still resolving. Consumers must not flash a disconnected state
   * (walls, banners, disabled rows) while true — render neutral/loading instead. */
  isLoading: boolean;
}

export interface PlatformConnectionInputs {
  userProfile: UserProfile | undefined;
  isProfileLoading: boolean;
  metaStatus: MetaIntegrationStatus | undefined;
  isMetaStatusLoading: boolean;
}

/**
 * Pure derivation behind ``usePlatformConnections`` (#5937), extracted so the per-platform
 * "connected" semantics are unit-testable without rendering. The rules:
 *
 * - Google is connected iff ``/user/me`` affirms ``google_ads_connected`` AND the token is not
 *   known-revoked (#5935). Absent fields (old cached profile, still loading) read as
 *   disconnected — ``isLoading`` is the signal consumers must use to avoid acting on that.
 * - Meta is connected iff OAuth is connected AND an ad account is selected — the same
 *   conditions the Meta wizard enforces. Entitlement is the backend's flag (#6678).
 */
export function derivePlatformConnections({
  userProfile,
  isProfileLoading,
  metaStatus,
  isMetaStatusLoading,
}: PlatformConnectionInputs): PlatformConnections {
  const googleBroken = userProfile?.google_connection_broken === true;
  const googleConnected = userProfile?.google_ads_connected === true && !googleBroken;
  const metaConnected = metaStatus?.is_connected === true && !!metaStatus.selected_ad_account;

  return {
    googleConnected,
    googleBroken,
    metaConnected,
    isLoading: isProfileLoading || isMetaStatusLoading,
  };
}
